import { Form as AntForm } from 'ant-design-vue';
import { createNamespacedHelpers } from 'vuex';

import Stepper from '@/components/Stepper';
import MainLayout from '@/components/layouts/MainLayout';
import { callFirst, sanitizeFormValues, normalizeFormData } from '@/common/helpers';
import { Button, PrimaryButton } from '@/components/common';
import {
  CustomerDetailsForm,
  HomeDetailsForm,
  AddOnsForm,
  CleaningTimesForm,
  EstimationSummary,
} from '@/components/estimate-forms';

const { mapActions, mapMutations, mapState } = createNamespacedHelpers('estimate');

export default {
  components: {
    AntForm,
    Stepper,
    MainLayout,
    Button,
    PrimaryButton,
  },
  data() {
    return {
      form: this.$form.createForm(this, {
        preserve: true,
      }),
      showSaveButton: false,
      savingEstimate: false,
      steps: [
        {
          component: CustomerDetailsForm,
          label: this.$t('estimate.steps.customerDetails.label'),
          heading: this.$t('estimate.steps.customerDetails.heading'),
          id: 'customerInfo',
        },
        {
          component: HomeDetailsForm,
          label: this.$t('estimate.steps.homeDetails.label'),
          heading: this.$t('estimate.steps.homeDetails.heading'),
          id: 'homeAreas',
          subheading: this.$t('estimate.steps.homeDetails.subheading'),
        },
        {
          component: AddOnsForm,
          label: this.$t('estimate.steps.addOns.label'),
          heading: this.$t('estimate.steps.addOns.heading'),
          id: 'addOns',
          subheading: this.$t('estimate.steps.homeDetails.subheading'),
        },
        {
          component: CleaningTimesForm,
          label: this.$t('estimate.steps.rate.label'),
          heading: this.$t('estimate.steps.rate.heading'),
          id: 'rate',
          beforeNextStep: this.handleSubmit,
          listeners: {
            hourlyRateChange: callFirst(this.updateRevisedRate, 'cleaners_amount'),
            cleanersAmountChange: callFirst(this.updateRevisedRate, 'hourly_rate'),
          },
        },
        {
          component: EstimationSummary,
          label: this.$t('estimate.steps.summary.label'),
          heading: this.$t('estimate.steps.summary.heading'),
          id: 'summary',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      estimate: state => state.current,
    }),
  },
  methods: {
    ...mapActions(['createEstimate']),
    ...mapMutations(['clearEstimate']),
    getSanitizedEstimateValues() {
      /**
       * `scheduleFields` are fields where we gather some additional info about cleaning service
       * after the estimation is calculated. We need this gimmick because the all the steps are
       * wrapped in 1 form, however the submit actually happens after step 4 of 5.
       * @todo Split the whole estimate form into separate forms to avoid hacks like the one below
       */
      const scheduleFields = [
        'cleaning_date',
        'cleaning_time',
        'payment_method',
        'entrance_method',
      ];
      return sanitizeFormValues({
        ...this.estimate.data,
        ...this.form.getFieldsValue(scheduleFields),
      });
    },
    handleSubmit() {
      return this.form.validateFields().then(async (values) => {
        await this.createEstimate({
          normalizeFormData: normalizeFormData(values),
          values,
        });
      });
    },
    handleStepChanged(stepId) {
      this.showSaveButton = stepId === this.steps[this.steps.length - 1].id;
    },
    updateRevisedRate(multiplierField, value) {
      const multiplier = this.form.getFieldValue(multiplierField);
      this.form.setFieldsValue({
        revised_hourly_rate: multiplier * value,
      });
    },
  },
  destroyed() {
    this.clearEstimate();
  },
};
